import api from './config/api';
import type {
    GetContactsParams,
    GetContactsResponse,
    UploadContactsRequest,
    UploadContactsResponse,
    DeleteContactsResponse,
    UpdateContactStatusRequest,
    UpdateContactStatusResponse,
    GetContactDetailsResponse,
} from 'types/contact.type';

export const uploadContacts = async (data: UploadContactsRequest): Promise<UploadContactsResponse> => {
    try {
        return await api.post<UploadContactsResponse>('/contacts', data).then((res) => res.data);
    } catch (error) {
        console.error('Error uploading contacts:', error);
        throw error;
    }
};

const normalizeStatusFilter = (status?: string | string[]): string[] | undefined => {
    if (!status) return undefined;

    const statuses = (Array.isArray(status) ? status : [status])
        .map((value) => value.trim())
        .filter((value) => value && value !== 'All Statuses');

    return statuses.length ? statuses : undefined;
};

export const getContacts = (params: GetContactsParams = {}): Promise<GetContactsResponse> => {
    const queryParams: Record<string, string | number | string[]> = {};

    if (params.page) queryParams.page = params.page;
    if (params.limit) queryParams.limit = params.limit;
    if (params.query && params.query.trim()) {
        queryParams.query = params.query.trim();
    }

    const statuses = normalizeStatusFilter(params.status);
    if (statuses) {
        queryParams.status = statuses.length === 1 ? statuses[0] : statuses;
    }

    if (params.workflowId) queryParams.workflowId = params.workflowId;
    if (params.sortBy) queryParams.sortBy = params.sortBy;

    return api
        .get<GetContactsResponse>('/contacts', {
            params: queryParams,
            paramsSerializer: {
                indexes: null,
            },
        })
        .then((res) => res.data);
};

export const deleteContact = (contactIds: string | string[]): Promise<DeleteContactsResponse> => {
    // If it's a single ID, convert to array
    const ids = Array.isArray(contactIds) ? contactIds : [contactIds];

    // Send the contactIds in the request payload
    return api
        .delete<DeleteContactsResponse>('/contacts', {
            data: { contactIds: ids },
        })
        .then((res) => res.data);
};

export const getContactDetails = (id: string): Promise<GetContactDetailsResponse> => {
    return api.get<GetContactDetailsResponse>(`/contacts/${id}`).then((res) => res.data);
};

export const updateContactStatus = (id: string, status: string, note: string): Promise<UpdateContactStatusResponse> => {
    const request: UpdateContactStatusRequest = { status, note };
    return api.put<UpdateContactStatusResponse>(`/contacts/status/${id}`, request).then((res) => res.data);
};

const contactService = {
    uploadContacts,
    getContacts,
    deleteContact,
    getContactDetails,
    updateContactStatus,
};

export default contactService;
